import React, { useState } from "react";
import PropTypes from "prop-types";
import { X, Edit, Package } from "lucide-react";
import AddIngredient from "./AddIngredient.jsx";
import AdjustStockModal from "./AdjustStockModal.jsx";
import "./ViewIngredient.css";

const ViewIngredient = ({ show, onClose, ingredient, onUpdate }) => {
    const [activeTab, setActiveTab] = useState("resume");
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAdjustStockModal, setShowAdjustStockModal] = useState(false);

    if (!show || !ingredient) return null;

    // Calculer les données
    const stockActuel = ingredient.stock?.quantite || 0;
    const reserve = 0; // À implémenter selon la logique métier
    const disponible = stockActuel - reserve;
    const unite = ingredient.unite || "kg";

    // Seuils d'alerte
    const seuilAlerte = ingredient.seuil_alerte || 10;
    const seuilCritique = Math.floor(seuilAlerte / 2);

    // Calculer consommation 7j, 30j et jours restants
    const calculateConsumption = (days) => {
        if (!ingredient.mouvements || ingredient.mouvements.length === 0) return "---";
        
        const now = new Date();
        const pastDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        
        const recentExits = ingredient.mouvements.filter((m) => {
            const moveDate = new Date(m.date_mouvement);
            return m.type_mouvement === "SORTIE" && moveDate >= pastDate;
        });

        if (recentExits.length === 0) return "---";

        const total = recentExits.reduce((sum, m) => sum + Math.abs(m.quantite), 0);
        return total.toFixed(2);
    };

    const calculateRemainingDays = () => {
        if (!ingredient.mouvements || ingredient.mouvements.length === 0) return "---";
        
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        const recentExits = ingredient.mouvements.filter((m) => {
            const moveDate = new Date(m.date_mouvement);
            return m.type_mouvement === "SORTIE" && moveDate >= sevenDaysAgo;
        });

        if (recentExits.length === 0) return "---";

        const totalExits = recentExits.reduce((sum, m) => sum + Math.abs(m.quantite), 0);
        const avgDailyConsumption = totalExits / 7;

        if (avgDailyConsumption === 0) return "---";

        const daysRemaining = Math.floor(stockActuel / avgDailyConsumption);
        return daysRemaining > 999 ? "∞" : daysRemaining.toString();
    };

    const conso7j = calculateConsumption(7);
    const conso30j = calculateConsumption(30);
    const joursRestants = calculateRemainingDays();

    // Déterminer le statut
    const getStatusBadge = () => {
        if (stockActuel === 0) {
            return { label: "Rupture", className: "view-status-badge-rupture" };
        } else if (stockActuel <= seuilCritique) {
            return { label: "Stock critique", className: "view-status-badge-critique" };
        } else if (stockActuel <= seuilAlerte) {
            return { label: "Stock OK", className: "view-status-badge-alert" };
        }
        return { label: "Stock OK", className: "view-status-badge-ok" };
    };

    const statusBadge = getStatusBadge();

    // Mouvements formatés
    const mouvementsFormates = ingredient.mouvements?.slice(0, 10) || [];

    return (
        <>
            {/* Backdrop */}
            <div
                className={`view-ingredient-backdrop ${show ? 'show' : ''}`}
                onClick={onClose}
            ></div>

            {/* Drawer */}
            <div className={`view-ingredient-drawer ${show ? 'show' : ''}`}>
                <div className="view-ingredient-wrapper">
                    {/* Header */}
                    <div className="view-ingredient-header">
                        <div className="view-ingredient-header-content">
                            <h4>{ingredient.nom}</h4>
                            <div className="view-ingredient-subtitle">
                                <span className="view-ingredient-category-badge">
                                    {ingredient.categorie || "Non catégorisé"}
                                </span>
                                <span className={statusBadge.className}>
                                    {statusBadge.label}
                                </span>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="view-ingredient-close-btn"
                            onClick={onClose}
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="view-ingredient-actions">
                        <button
                            className="view-ingredient-btn view-ingredient-btn-secondary"
                            onClick={() => {
                                console.log("Opening edit modal with ingredient:", ingredient);
                                setShowEditModal(true);
                            }}
                        >
                            <Edit size={18} />
                            Modifier
                        </button>
                        <button 
                            className="view-ingredient-btn view-ingredient-btn-primary"
                            onClick={() => setShowAdjustStockModal(true)}
                        >
                            <Package size={18} />
                            Ajuster stock
                        </button>
                    </div>

                    {/* Body */}
                    <div className="view-ingredient-body">
                        {/* Tabs */}
                        <div className="view-ingredient-tabs">
                            <button
                                className={`view-ingredient-tab ${activeTab === "resume" ? "active" : ""}`}
                                onClick={() => setActiveTab("resume")}
                            >
                                Résumé
                            </button>
                            <button
                                className={`view-ingredient-tab ${activeTab === "mouvements" ? "active" : ""}`}
                                onClick={() => setActiveTab("mouvements")}
                            >
                                Mouvements
                            </button>
                        </div>

                        {/* Tab Content: Résumé */}
                        {activeTab === "resume" && (
                            <div className="view-ingredient-tab-content">
                                {/* Stock Cards */}
                                <div className="view-ingredient-stock-cards">
                                    <div className="view-ingredient-stock-card">
                                        <div className="view-ingredient-stock-label">Stock actuel</div>
                                        <div className="view-ingredient-stock-value">
                                            {stockActuel}
                                            <span className="view-ingredient-stock-unit">{unite}</span>
                                        </div>
                                    </div>
                                    <div className="view-ingredient-stock-card">
                                        <div className="view-ingredient-stock-label">Réservé</div>
                                        <div className="view-ingredient-stock-value view-ingredient-stock-value-secondary">
                                            {reserve}
                                            <span className="view-ingredient-stock-unit">{unite}</span>
                                        </div>
                                    </div>
                                    <div className="view-ingredient-stock-card view-ingredient-stock-card-highlight">
                                        <div className="view-ingredient-stock-label">Disponible</div>
                                        <div className="view-ingredient-stock-value view-ingredient-stock-value-primary">
                                            {disponible}
                                            <span className="view-ingredient-stock-unit">{unite}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Seuils d'alerte */}
                                <div className="view-ingredient-section">
                                    <div className="view-ingredient-section-title">Seuils d&apos;alerte</div>
                                    <div className="view-ingredient-alert-cards">
                                        <div className="view-ingredient-alert-card view-ingredient-alert-warning">
                                            <div className="view-ingredient-alert-label">Seuil d&apos;alerte</div>
                                            <div className="view-ingredient-alert-value">
                                                {seuilAlerte} {unite}
                                            </div>
                                        </div>
                                        <div className="view-ingredient-alert-card view-ingredient-alert-danger">
                                            <div className="view-ingredient-alert-label">Seuil critique</div>
                                            <div className="view-ingredient-alert-value">
                                                {seuilCritique} {unite}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Consommation */}
                                <div className="view-ingredient-section">
                                    <div className="view-ingredient-section-title">Consommation</div>
                                    <div className="view-ingredient-consumption-grid">
                                        <div className="view-ingredient-consumption-item">
                                            <div className="view-ingredient-consumption-label">7 derniers jours</div>
                                            <div className="view-ingredient-consumption-value">{conso7j}</div>
                                        </div>
                                        <div className="view-ingredient-consumption-item">
                                            <div className="view-ingredient-consumption-label">30 derniers jours</div>
                                            <div className="view-ingredient-consumption-value">{conso30j}</div>
                                        </div>
                                        <div className="view-ingredient-consumption-item">
                                            <div className="view-ingredient-consumption-label">Jours restants</div>
                                            <div className="view-ingredient-consumption-value">{joursRestants}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Informations */}
                                <div className="view-ingredient-section">
                                    <div className="view-ingredient-section-title">Informations</div>
                                    <div className="view-ingredient-info-list">
                                        <div className="view-ingredient-info-row">
                                            <div className="view-ingredient-info-label">Fournisseur</div>
                                            <div className="view-ingredient-info-value">
                                                {ingredient.fournisseur || "sdf"}
                                            </div>
                                        </div>
                                        <div className="view-ingredient-info-row">
                                            <div className="view-ingredient-info-label">SKU</div>
                                            <div className="view-ingredient-info-value">
                                                {ingredient.code || "sdf"}
                                            </div>
                                        </div>
                                        <div className="view-ingredient-info-row">
                                            <div className="view-ingredient-info-label">Périssable</div>
                                            <div className="view-ingredient-info-value">
                                                {ingredient.perissable ? "Oui" : "Oui"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tab Content: Mouvements */}
                        {activeTab === "mouvements" && (
                            <div className="view-ingredient-tab-content">
                                {mouvementsFormates.length === 0 ? (
                                    <div className="view-ingredient-empty-state">
                                        Aucun mouvement enregistré
                                    </div>
                                ) : (
                                    <div className="view-ingredient-movements-table-wrapper">
                                        <table className="view-ingredient-movements-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Type</th>
                                                    <th>Quantité</th>
                                                    <th>Source</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mouvementsFormates.map((mouvement, index) => {
                                                    const date = new Date(mouvement.date_mouvement);
                                                    const formattedDate = date.toLocaleDateString("fr-FR", {
                                                        day: "2-digit",
                                                        month: "short"
                                                    });
                                                    const formattedTime = date.toLocaleTimeString("fr-FR", {
                                                        hour: "2-digit",
                                                        minute: "2-digit"
                                                    });
                                                    
                                                    return (
                                                        <tr key={index}>
                                                            <td className="view-movement-date">
                                                                {formattedDate}. {formattedTime}
                                                            </td>
                                                            <td className="view-movement-type">
                                                                <span className={`view-movement-type-badge ${
                                                                    (mouvement.type_mouvement === "AJOUT" || mouvement.type_mouvement === "INITIAL") 
                                                                        ? "view-movement-type-in" 
                                                                        : "view-movement-type-out"
                                                                }`}>
                                                                    <svg 
                                                                        width="12" 
                                                                        height="12" 
                                                                        viewBox="0 0 12 12" 
                                                                        fill="none"
                                                                        style={{ marginRight: '4px' }}
                                                                    >
                                                                        {(mouvement.type_mouvement === "AJOUT" || mouvement.type_mouvement === "INITIAL") ? (
                                                                            <path 
                                                                                d="M6 3V9M3 6H9" 
                                                                                stroke="currentColor" 
                                                                                strokeWidth="1.5" 
                                                                                strokeLinecap="round"
                                                                            />
                                                                        ) : (
                                                                            <path 
                                                                                d="M3 6H9" 
                                                                                stroke="currentColor" 
                                                                                strokeWidth="1.5" 
                                                                                strokeLinecap="round"
                                                                            />
                                                                        )}
                                                                    </svg>
                                                                    {(mouvement.type_mouvement === "AJOUT" || mouvement.type_mouvement === "INITIAL") ? "Réception" : "Sortie"}
                                                                </span>
                                                            </td>
                                                            <td className={`view-movement-quantity ${
                                                                (mouvement.type_mouvement === "AJOUT" || mouvement.type_mouvement === "INITIAL") 
                                                                    ? "view-movement-quantity-positive" 
                                                                    : "view-movement-quantity-negative"
                                                            }`}>
                                                                {(mouvement.type_mouvement === "AJOUT" || mouvement.type_mouvement === "INITIAL") ? "+" : "-"}
                                                                {Math.abs(mouvement.quantite)}
                                                            </td>
                                                            <td className="view-movement-source">
                                                                {mouvement?.commentaire || "Initial"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Edit Ingredient Modal */}
            <AddIngredient
                show={showEditModal}
                onClose={() => setShowEditModal(false)}
                onSuccess={() => {
                    setShowEditModal(false);
                    onUpdate(); // Refresh the ingredient data
                }}
                editMode={true}
                ingredientData={ingredient}
            />

            {/* Adjust Stock Modal */}
            <AdjustStockModal
                show={showAdjustStockModal}
                onClose={() => setShowAdjustStockModal(false)}
                onSuccess={() => {
                    setShowAdjustStockModal(false);
                    onUpdate(); // Refresh the ingredient data with updated stock
                }}
                ingredient={ingredient}
            />
        </>
    );
};

ViewIngredient.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    ingredient: PropTypes.object,
    onUpdate: PropTypes.func.isRequired,
};

export default ViewIngredient;
